import { AxiosRequestConfig } from "axios";

export interface RequestMiddleware<
  A extends {
    [key: string]: Function;
  } = any
> {
  preRequest?: () => void;
  postRequest?: () => void;
  actions?: A;
  hooks?: (() => void)[];
  config?: Partial<AxiosRequestConfig>;
  onError?: (error: Error) => void;
}

export interface DebounceAsyncParams {
  wait: number;
  maxWait?: number;
}

export function debounceAsync<
  T,
  Callback extends (...args: any[]) => Promise<T>
>(
  callback: Callback,
  debounce: DebounceAsyncParams
): (...args: Parameters<Callback>) => Promise<T> {
  let timer: number | null = null;
  let maxTimer: number | null = null;
  return (...args: any[]) => {
    if (timer) window.clearTimeout(timer);
    return new Promise<T>(resolve => {
      const timeoutPromise = new Promise<void>(resolve => {
        timer = window.setTimeout(() => {
          window.clearTimeout(maxTimer);
          maxTimer = null;
          resolve();
        }, debounce.wait);
        if (debounce.maxWait && !maxTimer) {
          maxTimer = window.setTimeout(() => {
            window.clearTimeout(timer);
            maxTimer = null;
            resolve();
          }, debounce.maxWait);
        }
      });
      timeoutPromise.then(async () => {
        resolve(await callback(...args));
      });
    });
  };
}

export type RequestMiddlewareActions<R> = R extends RequestMiddleware<infer A>
  ? A
  : never;

export function mergeRequestMiddleware<
  A extends { [key: string]: Function } = {}
>(middleware: RequestMiddleware[]): Omit<RequestMiddleware<A>, "config"> {
  return {
    preRequest() {
      middleware.forEach(({ preRequest }) => {
        if (preRequest) preRequest();
      });
    },
    postRequest() {
      middleware.forEach(({ postRequest }) => {
        if (postRequest) postRequest();
      });
    },
    actions: middleware.reduce(
      (prev, { actions }) => ({
        ...prev,
        ...actions
      }),
      {} as RequestMiddlewareActions<RequestMiddleware>
    ),
    onError(error) {
      middleware.forEach(({ onError }) => {
        if (onError) onError(error);
      });
    },
    hooks: middleware.reduce((prev, { hooks = [] }) => [...prev, ...hooks], [])
  };
}

export function mergeRequestUtilConfigs(
  middleware: RequestMiddleware[]
): RequestMiddleware["config"] {
  return middleware.reduce(
    (prev, { config }) => ({
      ...prev,
      ...config
    }),
    {} as RequestMiddleware["config"]
  );
}
